import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Plus } from "lucide-react";
import { useApiData, fmtDateTime } from "@/lib/api";
import { FullLoader } from "@/components/common/ProtectedRoute";
import { StatusBadge } from "@/components/common/StatusBadge";
import { PageTitle, EmptyState } from "@/components/shell/DashboardShell";
import { RequestTable } from "@/components/portal/RequestTable";
import { AttachmentList, DetailGrid, HistoryList, StatusTimeline } from "@/components/portal/RequestParts";
import { REQUEST_STATUSES, REQUEST_TYPES, STATUS } from "@/data/portal";

export function RequestList() {
  const { data, loading } = useApiData("/requests");
  const [filter, setFilter] = useState("all");
  if (loading) return <FullLoader />;
  const rows = (data || []).filter((r) => filter === "all" || r.status === filter);
  return (
    <div data-testid="portal-requests-page">
      <PageTitle eyebrow="Tracking" title="My requests" action={<Link to="/portal/new/service" className="btn-primary" data-testid="requests-new-button"><Plus className="h-4 w-4" /> New request</Link>} />
      <div className="mb-5 flex flex-wrap gap-2">
        {["all", ...REQUEST_STATUSES].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`requests-filter-${s}`} className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${filter === s ? "border-[#123634] bg-[#123634] text-white" : "border-slate-300 bg-white text-slate-600 hover:border-slate-500"}`}>
            {s === "all" ? "All" : STATUS[s].label}
          </button>
        ))}
      </div>
      <RequestTable rows={rows} basePath="/portal/requests" />
    </div>
  );
}

export const RequestBody = ({ r }) => {
  const cfg = REQUEST_TYPES[r.type];
  const detailRows = (cfg?.fields || []).map((fd) => [fd.label, r.details?.[fd.name]]);
  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="space-y-6 lg:col-span-2">
        <div className="card-base p-6"><StatusTimeline status={r.status} /></div>
        <div className="card-base space-y-6 p-6">
          <DetailGrid rows={[["Type", cfg?.label], ["Priority", r.priority], ["Site / location", r.site_location], ["Company", r.company_name], ...detailRows]} />
          <div>
            <p className="mono text-[11px] uppercase tracking-wider text-slate-400">Description</p>
            <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800" data-testid="request-description">{r.description}</p>
          </div>
        </div>
      </div>
      <div className="space-y-6">
        <div className="card-base p-6"><p className="mb-4 font-semibold text-slate-900">Activity</p><HistoryList history={r.history} /></div>
        <div className="card-base p-6"><p className="mb-4 font-semibold text-slate-900">Attachments</p><AttachmentList files={r.attachments} /></div>
      </div>
    </div>
  );
};

export function RequestDetail() {
  const { id } = useParams();
  const { data: r, loading, error } = useApiData(`/requests/${id}`);
  if (loading) return <FullLoader />;
  if (error || !r) return <EmptyState title="Request not found" text={error} testid="request-not-found" />;
  return (
    <div data-testid="portal-request-detail">
      <Link to="/portal/requests" className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900" data-testid="request-detail-back"><ArrowLeft className="h-4 w-4" /> All requests</Link>
      <PageTitle eyebrow={r.reference} title={r.subject} text={`Raised ${fmtDateTime(r.created_at)} · Last update ${fmtDateTime(r.updated_at)}`} action={<StatusBadge status={r.status} testid="request-detail-status" />} />
      <RequestBody r={r} />
    </div>
  );
}
